import {
  GenericFieldError,
  testOneKey,
} from "@common/validation/entityValidationUtils";
import { FlattenedLocation } from "@pages/Assets/LocationProvisioning/const";
import { getAllRequiredFields } from "./isRequired";
import { Location, LocationEntity } from "@services/locations/types";
import { sfcountries } from "@assets/countries";
import { sftimezones } from "@assets/timezones";
import { timeStringToDate } from "@pages/Assets/Locations/LocationDrawer/Content/utils/validation";
import { isValid } from "date-fns";

export enum LocationPropertyErrors {
  REQUIRED_FIELD_IS_EMPTY = "This field is required.",
  INVALID_OPTION = "Please select a valid option.",
  INVALID_LONGITUDE = "The longitude is invalid.",
  INVALID_LATITUDE = "The latitude is invalid.",
  INVALID_CLOSING_TIME = "The Closing Time is invalid.",
  INVALID_TIME = "The field must be empty if no opening days.",
}
export type FieldError = GenericFieldError<
  keyof FlattenedLocation,
  LocationPropertyErrors
>;
export type LocationFieldError<T = LocationPropertyErrors> = GenericFieldError<
  keyof FlattenedLocation,
  T
>;

export const getEmptyRequiredFields = (location: Location): FieldError[] =>
  getAllRequiredFields(location)
    .filter((x) => !testOneKey<Location, FlattenedLocation>(location, x))
    .map((property) => ({
      property,
      error: LocationPropertyErrors.REQUIRED_FIELD_IS_EMPTY,
    }));

export const getCoordinatesErrors = <T>(
  location: Location | LocationEntity
): LocationFieldError<T>[] => {
  const requiredFields = getAllRequiredFields(location).flat();

  const checkCoordinate = <T>(
    coordinate: keyof Required<Pick<Location, "latitude" | "longitude">>,
    limit: number,
    textError: T
  ) => {
    if (requiredFields.includes(coordinate)) {
      const value = Number(location[coordinate]);
      if (
        isNaN(value) ||
        Math.abs(value) > limit ||
        (coordinate === "longitude" && value === limit)
      ) {
        return [
          {
            property: coordinate,
            error: textError,
          },
        ];
      }
    }
    return [];
  };

  const longitudeErrors = checkCoordinate(
    "longitude",
    180,
    LocationPropertyErrors.INVALID_LONGITUDE as LocationPropertyErrors & T
  );
  const latitudeErrors = checkCoordinate(
    "latitude",
    90,
    LocationPropertyErrors.INVALID_LATITUDE as LocationPropertyErrors & T
  );

  return [...latitudeErrors, ...longitudeErrors];
};

export const getCountryErrors = (
  location: Location | LocationEntity
): LocationFieldError[] => {
  if (
    location.country !== undefined &&
    !sfcountries.find((country) => country.alpha2 == location.country)
  )
    return [
      { property: "country", error: LocationPropertyErrors.INVALID_OPTION },
    ];
  return [];
};

export const getTimezoneErrors = (
  location: Location | LocationEntity
): LocationFieldError[] => {
  if (
    location.details?.timeZone !== undefined &&
    !sftimezones.includes(location.details?.timeZone)
  )
    return [
      {
        property: "details.timeZone",
        error: LocationPropertyErrors.INVALID_OPTION,
      },
    ];
  return [];
};

export const getOpenCloseTimeErrors = (
  location: Location | LocationEntity
): LocationFieldError[] => {
  const schedule = location.details?.schedule;
  const openingTime = timeStringToDate(schedule?.from);
  const closingTime = timeStringToDate(schedule?.to);
  const hasValidTimes = isValid(openingTime) && isValid(closingTime);
  const hasOpeningTime = schedule?.from && isValid(openingTime);
  const hasClosingTime = schedule?.to && isValid(closingTime);

  if (schedule?.daysOfWeek?.length) {
    if (!hasOpeningTime && !hasClosingTime) {
      return [
        {
          property: "details.schedule.from",
          error: LocationPropertyErrors.INVALID_OPTION,
        },
        {
          property: "details.schedule.to",
          error: LocationPropertyErrors.INVALID_OPTION,
        },
      ];
    }
    if (!hasOpeningTime) {
      return [
        {
          property: "details.schedule.from",
          error: LocationPropertyErrors.INVALID_OPTION,
        },
      ];
    }
    if (!hasClosingTime) {
      return [
        {
          property: "details.schedule.to",
          error: LocationPropertyErrors.INVALID_OPTION,
        },
      ];
    }
  } else {
    if (hasValidTimes) {
      return [
        {
          property: "details.schedule.from",
          error: LocationPropertyErrors.INVALID_TIME,
        },
        {
          property: "details.schedule.to",
          error: LocationPropertyErrors.INVALID_TIME,
        },
      ];
    }
    if (hasOpeningTime) {
      return [
        {
          property: "details.schedule.from",
          error: LocationPropertyErrors.INVALID_TIME,
        },
      ];
    }
    if (hasClosingTime) {
      return [
        {
          property: "details.schedule.to",
          error: LocationPropertyErrors.INVALID_TIME,
        },
      ];
    }
  }
  return [];
};
export const getAllFieldsInError = (
  location: Partial<Location>
): FieldError[] => {
  return [
    getEmptyRequiredFields(location),
    getCoordinatesErrors<LocationPropertyErrors>(location),
    getTimezoneErrors(location),
    getCountryErrors(location),
    getOpenCloseTimeErrors(location),
  ].flat();
};
